import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '../../hooks/useTheme';
import mermaid from 'mermaid';

/**
 * Markdown renderer with syntax highlighting and Mermaid diagram support
 *
 * Renders markdown content with:
 * - Code syntax highlighting (supports dark/light mode)
 * - Mermaid diagram rendering
 * - Tailwind typography styling
 * - Custom component renderers for links, images, tables
 */

// Mermaid component for rendering diagrams
const MermaidDiagram = ({ chart, isDark }) => {
  const containerRef = useRef(null);
  const idRef = useRef(`mermaid-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    if (containerRef.current) {
      // Initialize mermaid with theme
      mermaid.initialize({
        startOnLoad: false,
        theme: isDark ? 'dark' : 'default',
        themeVariables: isDark ? {
          primaryColor: '#FFC000',
          primaryTextColor: '#fff',
          primaryBorderColor: '#FFC000',
          lineColor: '#FFC000',
          secondaryColor: '#0E3056',
          tertiaryColor: '#1a1a2e',
          background: '#1a1a2e',
          mainBkg: '#0E3056',
          nodeBorder: '#FFC000',
          clusterBkg: '#1a1a2e',
          titleColor: '#fff',
          edgeLabelBackground: '#1a1a2e',
        } : {
          primaryColor: '#FFC000',
          primaryTextColor: '#0E3056',
          primaryBorderColor: '#FFC000',
          lineColor: '#0E3056',
          secondaryColor: '#f0f0f0',
          tertiaryColor: '#fff',
        },
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
        },
        sequence: {
          useMaxWidth: true,
          diagramMarginX: 50,
          diagramMarginY: 10,
          actorMargin: 50,
          width: 150,
          height: 65,
          boxMargin: 10,
          boxTextMargin: 5,
          noteMargin: 10,
          messageMargin: 35,
        },
      });

      // Render the diagram
      mermaid.render(idRef.current, chart).then(({ svg }) => {
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      }).catch((error) => {
        console.error('Mermaid rendering error:', error);
        if (containerRef.current) {
          containerRef.current.innerHTML = `<pre class="text-red-500">Error rendering diagram: ${error.message}</pre>`;
        }
      });
    }
  }, [chart, isDark]);

  return (
    <div
      ref={containerRef}
      className="my-6 flex justify-center overflow-x-auto"
    />
  );
};

const MarkdownRenderer = ({ content, className = '' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Custom renderers for markdown elements
  const components = {
    // Code blocks with syntax highlighting or Mermaid rendering
    code({ node, inline, className: codeClassName, children, ...props }) {
      const match = /language-(\w+)/.exec(codeClassName || '');
      const language = match ? match[1] : '';
      const codeContent = String(children).replace(/\n$/, '');

      // Handle Mermaid diagrams
      if (!inline && language === 'mermaid') {
        return <MermaidDiagram chart={codeContent} isDark={isDark} />;
      }

      if (!inline && language) {
        return (
          <SyntaxHighlighter
            style={isDark ? oneDark : oneLight}
            language={language}
            PreTag="div"
            className="rounded-lg my-4 text-sm"
            showLineNumbers={true}
            {...props}
          >
            {codeContent}
          </SyntaxHighlighter>
        );
      }

      // Inline code
      return (
        <code
          className={`px-1.5 py-0.5 rounded text-sm font-mono ${
            isDark
              ? 'bg-gray-700 text-primary-400'
              : 'bg-gray-100 text-primary-600'
          }`}
          {...props}
        >
          {children}
        </code>
      );
    },

    // Links - open external links in new tab
    a({ href, children, ...props }) {
      const isExternal = href?.startsWith('http');
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="text-primary-600 dark:text-primary-400 hover:underline"
          {...props}
        >
          {children}
        </a>
      );
    },

    // Tables with styling
    table({ children, ...props }) {
      return (
        <div className="overflow-x-auto my-4">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" {...props}>
            {children}
          </table>
        </div>
      );
    },

    thead({ children, ...props }) {
      return (
        <thead className="bg-gray-50 dark:bg-gray-800" {...props}>
          {children}
        </thead>
      );
    },

    th({ children, ...props }) {
      return (
        <th
          className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          {...props}
        >
          {children}
        </th>
      );
    },

    td({ children, ...props }) {
      return (
        <td
          className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700"
          {...props}
        >
          {children}
        </td>
      );
    },

    // Blockquotes / Admonitions
    blockquote({ children, ...props }) {
      return (
        <blockquote
          className="border-l-4 border-primary-500 pl-4 py-2 my-4 bg-gray-50 dark:bg-gray-800 rounded-r-lg"
          {...props}
        >
          {children}
        </blockquote>
      );
    },

    // Headings with anchor links
    h1({ children, ...props }) {
      const id = String(children).toLowerCase().replace(/\s+/g, '-');
      return (
        <h1
          id={id}
          className="text-3xl font-bold text-gray-900 dark:text-white mt-8 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
          {...props}
        >
          {children}
        </h1>
      );
    },

    h2({ children, ...props }) {
      const id = String(children).toLowerCase().replace(/\s+/g, '-');
      return (
        <h2
          id={id}
          className="text-2xl font-semibold text-gray-900 dark:text-white mt-6 mb-3"
          {...props}
        >
          {children}
        </h2>
      );
    },

    h3({ children, ...props }) {
      const id = String(children).toLowerCase().replace(/\s+/g, '-');
      return (
        <h3
          id={id}
          className="text-xl font-semibold text-gray-800 dark:text-gray-100 mt-5 mb-2"
          {...props}
        >
          {children}
        </h3>
      );
    },

    h4({ children, ...props }) {
      return (
        <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2" {...props}>
          {children}
        </h4>
      );
    },

    // Paragraphs
    p({ children, ...props }) {
      return (
        <p className="text-gray-600 dark:text-gray-300 my-3 leading-relaxed" {...props}>
          {children}
        </p>
      );
    },

    // Lists
    ul({ children, ...props }) {
      return (
        <ul className="list-disc list-inside my-3 space-y-1 text-gray-600 dark:text-gray-300" {...props}>
          {children}
        </ul>
      );
    },

    ol({ children, ...props }) {
      return (
        <ol className="list-decimal list-inside my-3 space-y-1 text-gray-600 dark:text-gray-300" {...props}>
          {children}
        </ol>
      );
    },

    li({ children, ...props }) {
      return (
        <li className="ml-4" {...props}>
          {children}
        </li>
      );
    },

    // Horizontal rule
    hr({ ...props }) {
      return (
        <hr className="my-8 border-gray-200 dark:border-gray-700" {...props} />
      );
    },

    // Images
    img({ src, alt, ...props }) {
      return (
        <img
          src={src}
          alt={alt}
          className="max-w-full h-auto rounded-lg shadow-md my-4"
          {...props}
        />
      );
    }
  };

  return (
    <div className={`prose prose-gray dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
